import { useCallback, useEffect, useState } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import { performGetRequest } from '@atlassian/jira-fetch';
import { useIssueKey } from '@atlassian/jira-issue-context-service';
import { useProjectKey } from '@atlassian/jira-project-context-service';
import type { FetchCreateMetaPromise, UseDefaultPriorityService, DefaultValue } from './types';

const getCreateMeta = async (projectIdOrKey: string, issueTypeId: string) => {
	const resp = await performGetRequest(
		`/rest/api/3/issue/createmeta/${projectIdOrKey}/issuetypes/${issueTypeId}`,
	);
	return resp;
};

export const useIssueType = () => useState<string | undefined>();

export const useDefaultPriority: UseDefaultPriorityService = () => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const [defaultPriority, setDefaultPriority] = useState<DefaultValue | undefined>();
	const [issueType, setIssueType] = useIssueType();
	const fetchCreateMetaService: FetchCreateMetaPromise = useCallback(
		(issueTypeId: string) => getCreateMeta(projectKey, issueTypeId),
		[projectKey],
	);
	const { loading, error, data, fetch: fetchCreateMeta } = useService(fetchCreateMetaService);

	const fetchDefaultPriority = useCallback(
		(issueTypeId: string) => {
			if (!loading && issueTypeId !== issueType) {
				fetchCreateMeta(issueTypeId);
				setIssueType(issueTypeId);
			}
		},
		[loading, issueType, fetchCreateMeta, setIssueType],
	);

	useEffect(() => {
		if (data && Array.isArray(data.fields)) {
			const priorityField = data.fields.find(
				(field) => field.hasDefaultValue && field.fieldId === 'priority',
			);
			// Set default priority, for given issue type, if there is none, it's none
			setDefaultPriority(priorityField?.defaultValue ?? undefined);
		}
	}, [data]);

	return [{ loading, error, data: defaultPriority }, fetchDefaultPriority];
};
